import { TextInput } from '@mantine/core'

import { isDialableUsNumber, sanitizeEmail } from 'src/lib/contact.utils'

export type ContactDraft = {
  contactName: string
  email: string
  phoneNumber: string
}

export { isDialableUsNumber } from 'src/lib/contact.utils'

/**
 * Errors for a draft, judged against the values it started from.
 *
 * A field that was already invalid when the row opened is left alone: the
 * directory holds numbers recorded before these rules existed, and refusing to
 * save around one would block every other correction on that row. Only what
 * somebody actually typed has to pass.
 */
export const draftErrors = (draft: ContactDraft, original?: ContactDraft) => {
  const unchanged = (field: keyof ContactDraft) =>
    original !== undefined && draft[field].trim() === original[field].trim()

  return {
    email:
      unchanged('email') || !draft.email.trim() || sanitizeEmail(draft.email)
        ? null
        : 'Enter a valid email address, or clear it',
    phoneNumber:
      unchanged('phoneNumber') ||
      !draft.phoneNumber.trim() ||
      isDialableUsNumber(draft.phoneNumber)
        ? null
        : 'Enter a ten-digit US number, or clear it',
  }
}

export const draftHasErrors = (draft: ContactDraft, original?: ContactDraft) =>
  Object.values(draftErrors(draft, original)).some(Boolean)

/** One editable cell in a contact row. */
export const ContactCellInput = ({
  value,
  onChange,
  error,
  placeholder,
  mono = false,
}: {
  value: string
  onChange: (value: string) => void
  error?: string | null
  placeholder: string
  mono?: boolean
}) => (
  <TextInput
    size="xs"
    value={value}
    onChange={(event) => onChange(event.currentTarget.value)}
    error={error || undefined}
    placeholder={placeholder}
    aria-label={placeholder}
    classNames={mono ? { input: 'font-mono text-xs' } : { input: 'text-xs' }}
  />
)
